import { useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  FileText, Plus, Trash2, Eye, Download, Pencil, Users, ShieldCheck, EyeOff, UploadCloud, CheckCircle2, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { useFileDrop } from "@/lib/useFileDrop";

const CUSTOM = "__custom__";

function fmtSize(b) {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function fileHref(id) {
  const token = localStorage.getItem("fl_token") || "";
  return `${API}/company-documents/${id}/file?auth=${encodeURIComponent(token)}`;
}

export default function CompanyDocuments({ category = null }) {
  const [data, setData] = useState({ documents: [], categories: [], preset_categories: [], field_worker_count: 0 });
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [acksDoc, setAcksDoc] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/company-documents", { params: category ? { category } : {} });
      setData(data);
    } catch {
      toast.error("Couldn't load company documents");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [category]);

  const grouped = useMemo(() => {
    const g = {};
    for (const d of data.documents) {
      (g[d.category] = g[d.category] || []).push(d);
    }
    return g;
  }, [data.documents]);

  return (
    <div className="space-y-6" data-testid="company-documents-page">
      <PageHeader
        eyebrow="Company"
        title={category === "Health & Safety" ? "Safety Documents" : "Company Documents"}
        subtitle="Upload policies, health & safety, inductions and forms. Choose what your field crew can see in their app."
        action={
          data.documents.length > 0 ? (
            <Button onClick={() => setUploadOpen(true)} className="bg-[#FF5F15] hover:bg-[#e5560f] text-white" data-testid="upload-document-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Upload document
            </Button>
          ) : null
        }
      />

      {loading ? (
        <div className="text-sm text-zinc-500 dark:text-zinc-400">Loading…</div>
      ) : data.documents.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No documents yet"
          description="Upload your first company policy or H&S document to share it with your crew."
          action={
            <Button onClick={() => setUploadOpen(true)} className="bg-[#FF5F15] hover:bg-[#e5560f] text-white" data-testid="upload-document-empty-btn">
              <UploadCloud className="w-4 h-4 mr-1.5" /> Upload document
            </Button>
          }
        />
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([cat, docs]) => (
            <div key={cat} data-testid={`doc-category-${cat}`}>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FF5F15] mb-3">{cat}</div>
              <div className="grid gap-3">
                {docs.map((d) => (
                  <DocRow
                    key={d.id}
                    doc={d}
                    fieldCount={data.field_worker_count}
                    onChanged={load}
                    onEdit={() => setEditDoc(d)}
                    onAcks={() => setAcksDoc(d)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        categories={data.categories}
        defaultCategory={category}
        onDone={load}
      />
      {editDoc && (
        <EditDialog doc={editDoc} categories={data.categories} onOpenChange={(v) => !v && setEditDoc(null)} onDone={load} />
      )}
      {acksDoc && (
        <AcksDialog doc={acksDoc} onOpenChange={(v) => !v && setAcksDoc(null)} />
      )}
    </div>
  );
}

function DocRow({ doc, fieldCount, onChanged, onEdit, onAcks }) {
  const [busy, setBusy] = useState(false);
  const toggleVisible = async () => {
    setBusy(true);
    try {
      await api.patch(`/company-documents/${doc.id}`, { visible_to_field: !doc.visible_to_field });
      onChanged();
    } catch {
      toast.error("Couldn't update visibility");
    } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete "${doc.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/company-documents/${doc.id}`);
      toast.success("Document deleted");
      onChanged();
    } catch {
      toast.error("Couldn't delete document");
    }
  };
  return (
    <div className="hard-card p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`doc-row-${doc.id}`}>
      <div className="w-10 h-10 rounded-xl bg-[#FF5F15]/10 text-[#FF5F15] flex items-center justify-center shrink-0">
        <FileText className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-zinc-900 dark:text-white truncate" data-testid={`doc-title-${doc.id}`}>{doc.title}</div>
        <div className="text-[12px] text-zinc-500 dark:text-zinc-400 flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5">
          <span className="truncate max-w-[180px]">{doc.original_filename}</span>
          <span>·</span>
          <span>{fmtSize(doc.size)}</span>
          <span>·</span>
          <button onClick={onAcks} className="inline-flex items-center gap-1 hover:text-[#FF5F15]" data-testid={`doc-acks-${doc.id}`}>
            <Users className="w-3.5 h-3.5" /> {doc.ack_count}/{fieldCount} read
          </button>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge variant={doc.visible_to_field ? "default" : "secondary"} className={doc.visible_to_field ? "bg-green-600 hover:bg-green-600" : ""}>
          {doc.visible_to_field ? <ShieldCheck className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
          {doc.visible_to_field ? "Crew can see" : "Hidden"}
        </Badge>
        <Switch checked={!!doc.visible_to_field} disabled={busy} onCheckedChange={toggleVisible} data-testid={`doc-visible-toggle-${doc.id}`} />
        <a href={fileHref(doc.id)} target="_blank" rel="noreferrer" className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-300" title="View" data-testid={`doc-view-${doc.id}`}>
          <Eye className="w-4 h-4" />
        </a>
        <a href={`${fileHref(doc.id)}&download=1`} className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-300" title="Download" data-testid={`doc-download-${doc.id}`}>
          <Download className="w-4 h-4" />
        </a>
        <button onClick={onEdit} className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-300" title="Edit" data-testid={`doc-edit-${doc.id}`}>
          <Pencil className="w-4 h-4" />
        </button>
        <button onClick={del} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 text-red-600" title="Delete" data-testid={`doc-delete-${doc.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function CategoryPicker({ value, onChange, categories }) {
  const [custom, setCustom] = useState(false);
  return custom ? (
    <div className="flex gap-2">
      <Input autoFocus placeholder="New category name" value={value} onChange={(e) => onChange(e.target.value)} data-testid="doc-category-custom-input" />
      <Button type="button" variant="outline" onClick={() => { setCustom(false); onChange(categories[0] || "Other"); }}>Cancel</Button>
    </div>
  ) : (
    <Select value={categories.includes(value) ? value : ""} onValueChange={(v) => { if (v === CUSTOM) { setCustom(true); onChange(""); } else onChange(v); }}>
      <SelectTrigger data-testid="doc-category-select"><SelectValue placeholder="Choose a category" /></SelectTrigger>
      <SelectContent>
        {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        <SelectItem value={CUSTOM}>+ Add new category…</SelectItem>
      </SelectContent>
    </Select>
  );
}

function UploadDialog({ open, onOpenChange, categories, onDone, defaultCategory }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(defaultCategory || categories[0] || "Company Policies");
  const [visible, setVisible] = useState(true);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setTitle(""); setCategory(categories[0] || "Company Policies"); setVisible(true); setFile(null); } }, [open, categories]);

  const submit = async () => {
    if (title.trim().length < 2) return toast.error("Please add a title");
    if (!category.trim()) return toast.error("Please choose a category");
    if (!file) return toast.error("Please choose a file");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title.trim());
    fd.append("category", category.trim());
    fd.append("visible_to_field", visible ? "true" : "false");
    try {
      await api.post("/company-documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="upload-document-dialog">
        <DialogHeader>
          <DialogTitle>Upload company document</DialogTitle>
          <DialogDescription>Share policies, H&S, inductions and forms with your team.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Health & Safety Policy 2026" data-testid="doc-title-input" />
          </div>
          <div>
            <Label>Category</Label>
            <CategoryPicker value={category} onChange={setCategory} categories={categories} />
          </div>
          <div>
            <Label>File</Label>
            <DocDropzone file={file} onFile={setFile} />
            <p className="text-[11px] text-zinc-500 mt-1">PDF, image or Office document · max 15MB</p>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-zinc-200 dark:border-zinc-700 p-3">
            <div>
              <div className="text-sm font-medium text-zinc-900 dark:text-white">Visible to field workers</div>
              <div className="text-[12px] text-zinc-500">Show this in the crew's "My Company" area</div>
            </div>
            <Switch checked={visible} onCheckedChange={setVisible} data-testid="doc-visible-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#FF5F15] hover:bg-[#e5560f] text-white" data-testid="doc-upload-submit">
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocDropzone({ file, onFile }) {
  const inputRef = useRef(null);
  const { dragging, dropProps } = useFileDrop((files) => onFile(files?.[0] || null));
  const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv";
  return (
    <div
      {...dropProps}
      onClick={() => inputRef.current?.click()}
      className={`mt-1 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center cursor-pointer transition-colors ${
        dragging ? "border-[#FF5F15] bg-orange-50" : "border-zinc-300 dark:border-zinc-700 hover:border-[#FF5F15] hover:bg-orange-50/40"
      }`}
      data-testid="doc-dropzone"
    >
      <UploadCloud className={`w-7 h-7 ${dragging ? "text-[#FF5F15]" : "text-zinc-400"}`} />
      {file ? (
        <div className="text-sm font-medium text-zinc-900 dark:text-white flex items-center gap-1.5">
          <CheckCircle2 className="w-4 h-4 text-green-600" /> {file.name}
        </div>
      ) : (
        <div className="text-sm text-zinc-600 dark:text-zinc-300">
          <span className="font-semibold text-[#FF5F15]">Drag &amp; drop</span> a file here, or <span className="underline">choose file</span>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0] || null)}
        data-testid="doc-file-input"
      />
    </div>
  );
}

function EditDialog({ doc, categories, onOpenChange, onDone }) {
  const [title, setTitle] = useState(doc.title);
  const [category, setCategory] = useState(doc.category);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/company-documents/${doc.id}`, { title: title.trim(), category: category.trim() });
      toast.success("Saved");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-document-dialog">
        <DialogHeader><DialogTitle>Edit document</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="edit-doc-title" /></div>
          <div><Label>Category</Label><CategoryPicker value={category} onChange={setCategory} categories={categories} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#FF5F15] hover:bg-[#e5560f] text-white" data-testid="edit-doc-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AcksDialog({ doc, onOpenChange }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/company-documents/${doc.id}/acknowledgements`).then(({ data }) => setData(data)).catch(() => setData({ acknowledged: [], pending_field_workers: [] }));
  }, [doc.id]);
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="acks-dialog">
        <DialogHeader>
          <DialogTitle>Who's read "{doc.title}"</DialogTitle>
          <DialogDescription>Track which field workers have opened and acknowledged this document.</DialogDescription>
        </DialogHeader>
        {!data ? <div className="text-sm text-zinc-500">Loading…</div> : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-green-600 mb-2 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Read ({data.acknowledged.length})</div>
              {data.acknowledged.length === 0 ? <p className="text-sm text-zinc-500">No one yet.</p> : (
                <ul className="space-y-1">
                  {data.acknowledged.map((a) => (
                    <li key={a.user_id} className="flex items-center justify-between text-sm">
                      <span className="text-zinc-800 dark:text-zinc-200">{a.user_name}</span>
                      <span className="text-[12px] text-zinc-500">{(a.acknowledged_at || "").slice(0, 10)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-amber-600 mb-2 flex items-center gap-1.5"><Clock className="w-4 h-4" /> Not yet read ({data.pending_field_workers.length})</div>
              {data.pending_field_workers.length === 0 ? <p className="text-sm text-zinc-500">Everyone's up to date.</p> : (
                <ul className="space-y-1">
                  {data.pending_field_workers.map((w) => (
                    <li key={w.id} className="text-sm text-zinc-600 dark:text-zinc-400">{w.name}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
